// Copyright (c) StrangeBee 2023

import React, { ReactElement, useMemo, useState } from 'react';
import { Comment } from 'antd';
import { TaskLogOutput, TaskLogInput, TaskOutput, TaskStatus, PermissionsEnum } from 'thehive-sdk';
import { findUser, useLocalMessage } from '@Helpers/';
import FormatDate from '@Molecules/Format/FormatDate/FormatDate';
import { useListAllUsers } from '@Queries/userQueries';
import { ResponderDrawer } from '@Organisms/Responder';
import { SystemUser } from '@Constants/organisation';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import DeleteButton from '@Atoms/Buttons/DeleteButton/DeleteButton';
import { TaskLogManage } from '../TaskLogManage/TaskLogManage';
import TaskLogItemReports from '../TaskLogItemReports/TaskLogItemReports';
import TaskLogFile from '../TaskLogFile/TaskLogFile';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { ICONS } from '@Constants/iconsApp';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { onCaseImageUpload } from '@Helpers/download';
import { TaskEventList } from '@Organisms/Task/eventList';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';

import './TaskLogItem.scss';

interface TaskLogItemProps {
    taskLog: TaskLogOutput;
    deleteTaskLog: (taskLogId: string) => void;
    editTaskLog: (taskLog: TaskLogInput) => Promise<any>;
    enableResponder?: boolean;
    caseId: string;
    taskId: string;
    taskEventList: TaskEventList;
    task?: TaskOutput;
    disableDeletion?: boolean;
}

const TaskLogItem = ({
    taskLog,
    deleteTaskLog,
    editTaskLog,
    enableResponder = true,
    caseId,
    taskId,
    taskEventList,
    task,
    disableDeletion = false,
}: TaskLogItemProps): ReactElement => {
    const i = useLocalMessage();
    const { data: allUsersList = [] } = useListAllUsers();

    const [showReports, setShowReports] = useState(false);
    const {
        _id,
        message,
        attachments,
        includeInTimeline,
        _createdAt,
        _createdBy,
        extraData: { actionCount = 0 },
    } = taskLog || {};

    const assigneeUser = useMemo(
        () => (_createdBy !== SystemUser.login ? findUser(_createdBy, allUsersList) : SystemUser),
        [_createdBy, allUsersList],
    );

    const { name = '', avatar = '' } = assigneeUser || {};

    const onEditFormFinish = (input: TaskLogInput): Promise<any> => {
        return editTaskLog(input);
    };

    const toggleReports = (): void => {
        setShowReports((reports) => !reports);
    };

    const { extraData: { case: { userPermissions = [] } = {} } = {} } = task || {};

    const hasPermissions = useSplitPermissions(userPermissions, [
        PermissionsEnum.MANAGE_TASK,
        PermissionsEnum.MANAGE_ACTION,
    ]);

    const actions = [
        hasPermissions.task ? (
            <TaskLogManage
                onTaskClose={() =>
                    taskEventList.onEdit(
                        {
                            status: TaskStatus.COMPLETED,
                        },
                        taskId,
                    )
                }
                task={task}
                key={`${_id}-tasklog-manage`}
                triggerElem={
                    <THTooltip key="comment-basic-edit" title={i('task-log.actions.edit')}>
                        <ICONS.EditIcon />
                    </THTooltip>
                }
                onFormFinish={onEditFormFinish}
                taskLog={taskLog}
            />
        ) : null,
        enableResponder && hasPermissions.action ? (
            <ResponderDrawer
                key={`${_id}-tasklog-responders`}
                objectId={_id}
                objectType="case_task_log"
                triggerElem={
                    <THTooltip key="comment-basic-dislike" title={i('task-log.actions.responder')}>
                        <ICONS.SettingIcon />
                    </THTooltip>
                }
            />
        ) : null,
        hasPermissions.task ? (
            <THTooltip key={`${_id}-tasklog-delete`} title={i('task-log.actions.remove')}>
                <span className="comment-action">
                    {
                        <DeleteButton
                            key="tasklog-delete"
                            label={i('task.logs.item.deleteText')}
                            className="delete-button"
                            disabled={disableDeletion}
                            onClick={(event): void => event.stopPropagation()}
                            onConfirm={(): void => deleteTaskLog(_id)}
                        />
                    }
                </span>
            </THTooltip>
        ) : null,
        actionCount ? (
            <span key={`${_id}-tasklog-action`} onClick={toggleReports}>
                {`${i(`task-log.responders.${showReports ? 'hide' : 'show'}`)}`}
            </span>
        ) : null,
    ];

    return (
        <>
            <Comment
                actions={actions}
                className="comment-item"
                author={
                    <>
                        <span className="comment-author">{name}</span>
                        <FormatDate date={_createdAt} className="comment-created-at" />
                        {includeInTimeline ? (
                            <span className="comment-include">
                                <span className="comment-include__label">{i('task-log.field.includeInTimeline')}</span>
                                <FormatDate date={includeInTimeline} />
                            </span>
                        ) : null}
                    </>
                }
                avatar={<UserAvatar size="small" avatar={avatar} name={name} />}
                content={
                    <>
                        <THMarkdown value={message} disabled borderless onImageUpload={onCaseImageUpload(caseId)} />
                        <div className="tasklog-attachement__list">
                            {(attachments as any[]).map((attachment) => {
                                return <TaskLogFile key={attachment._id} attachment={attachment} taskLogId={_id} />;
                            })}
                        </div>
                    </>
                }
            />

            {showReports && <TaskLogItemReports taskLogId={_id} key="tasklog-report" className="task-log__report" />}
        </>
    );
};

export default TaskLogItem;
